import React, { useCallback } from 'react';

import { Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { Calories } from 'components/price-and-calories';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import { MenuAcceleratorBadge } from '../menu-accelerator-badge';

import {
  PriceAndCaloriesListWrapper,
  Tile,
  TileDescription,
  TileHeader,
  TileImage,
  TileImageWrapper,
} from './styled';
import { IMenuTileProps, IPriceAndCaloriesProps } from './types-badges-experiment';

const ItemCalories = Text.withConfig({
  numberOfLines: 1,
  color: Styles.color.tertiary,
  marginTop: '1',
  marginBottom: '0',
  textTransform: 'capitalize',
});

const StyledItemPrice = Text.withConfig({
  fontWeight: 'bold',
  color: Styles.color.pricing,
  marginTop: '1',
  marginRight: {
    base: '1',
    md: '0',
  },
});

const priceAndCalories = ({
  calories,
  price,
  type,
}: IPriceAndCaloriesProps): JSX.Element | null => {
  const shouldRenderPriceAndCalories = price || calories;
  if (type === 'section' || !shouldRenderPriceAndCalories) {
    return null;
  }

  return (
    <PriceAndCaloriesListWrapper>
      <StyledItemPrice>{price}</StyledItemPrice>
      <Calories>
        <ItemCalories>{calories}</ItemCalories>
      </Calories>
    </PriceAndCaloriesListWrapper>
  );
};

const MenuTile = React.memo(
  ({
    pathname,
    image,
    objectFitContain,
    calories,
    name,
    type,
    price,
    id,
    accessibilityLabel = '',
    accessibilityLevel = 3,
    accessibilityRole = 'header',
    showBadge = false,
  }: IMenuTileProps) => {
    const { formatMessage } = useIntl();

    const priceAndCaloriesContent = priceAndCalories({ type, price, calories });

    const handleOnImageLoaded = useCallback(() => {
      emitEvent(EventName.MENU_TILE_IMAGE_LOADED, {
        sanityId: id,
        isOnMainMenu: pathname === routes.menu,
        attributes: [],
      });
    }, [id, pathname]);

    return (
      <Tile
        testID="menu-tile-bk"
        accessibilityRole={accessibilityRole}
        accessibilityLevel={accessibilityLevel}
        accessibilityLabel={accessibilityLabel}
      >
        <TileImageWrapper>
          {!!showBadge && <MenuAcceleratorBadge position="absolute" />}
          <TileImage
            image={image}
            alt={''}
            objectFitContain={objectFitContain}
            onImageLoaded={handleOnImageLoaded}
            accessibilityHint={formatMessage({ id: 'select' })}
          />
        </TileImageWrapper>
        <TileDescription>
          <TileHeader testID={`tile-header-${name?.toLowerCase().replace(/\s/g, '')}`}>
            {name}
          </TileHeader>
          {priceAndCaloriesContent}
        </TileDescription>
      </Tile>
    );
  }
);

export default MenuTile;
